import Header from "@/components/layouts/Header";
import Map from "@/components/Map";
import DateTimeField from "@/components/ui/date-time-field";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { ReportButton } from "@/components/ui/report-button";
import VehicleSelect from "@/components/ui/vehicle-select";
import { MapStateProvider } from "@/contexts/MapStateContext";
import type { ClientEvents, TrackerPosition } from "@/interfaces";
import { errorHandler } from "@/lib/utils";
import { trackReportsService } from "@/services/api/trackReports";
import { zodResolver } from "@hookform/resolvers/zod";
import { differenceInDays, format, isValid, parse } from "date-fns";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

// Helper function to parse date strings consistently
const parseFormDate = (dateString: string) => {
  // Try to parse as yyyy-MM-dd HH:mm (form format)
  let date = parse(dateString, "yyyy-MM-dd HH:mm", new Date());

  // If that fails, try parsing as a regular date string
  if (!isValid(date)) {
    date = new Date(dateString);
  }

  return date;
};

const formSchema = z
  .object({
    vehicle_id: z.number({
      message: "Veículo é obrigatório",
    }),
    start_period: z.string({
      error: "Período inicial é obrigatório",
    }),
    end_period: z.string({
      error: "Período final é obrigatório",
    }),
  })
  .refine(
    (data) => {
      const startDate = parseFormDate(data.start_period);
      const endDate = parseFormDate(data.end_period);

      if (!isValid(startDate) || !isValid(endDate)) {
        return false;
      }

      // Make sure end date is after or equal to start date
      if (endDate < startDate) {
        return false;
      }

      // Check if difference is no more than 4 days
      const daysDiff = differenceInDays(endDate, startDate);
      return daysDiff <= 4;
    },
    {
      message: "O período máximo permitido é de 4 dias",
      path: ["end_period"],
    },
  )
  .refine(
    (data) => {
      const startDate = parseFormDate(data.start_period);
      const endDate = parseFormDate(data.end_period);

      return endDate >= startDate;
    },
    {
      message: "Data final deve ser posterior à data inicial",
      path: ["end_period"],
    },
  );

type FormValues = z.infer<typeof formSchema>;

const MapReport = () => {
  const [mapData, setMapData] = useState<TrackerPosition[]>([]);
  const [driversWithColors, setDriversWithColors] = useState<
    Array<{
      name: string;
      color: string;
    }>
  >([]);
  const [events, setEvents] = useState<ClientEvents[]>([]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      vehicle_id: undefined,
      start_period: format(new Date(), "yyyy-MM-dd 00:00"),
      end_period: format(new Date(), "yyyy-MM-dd 23:59"),
    },
  });

  const onSubmit = async (data: FormValues) => {
    try {
      const formatDateTime = (dateTimeString: string) => {
        let dateObj;

        dateObj = parse(dateTimeString, "dd/MM/yyyy HH:mm", new Date());

        if (!isValid(dateObj)) {
          dateObj = new Date(dateTimeString);
        }

        return {
          date: format(dateObj, "yyyy-MM-dd"),
          time: format(dateObj, "HH:mm"),
        };
      };

      const start = formatDateTime(data.start_period);
      const end = formatDateTime(data.end_period);

      const params = {
        vehicle_id: data.vehicle_id,
        start_date: start.date,
        start_hour: start.time,
        end_date: end.date,
        end_hour: end.time,
      };

      const response = await trackReportsService.getMapReport(params);
      let positions: TrackerPosition[] = [];
      let allDrivers: Array<{ name: string; color: string }> = [];

      if (response.data && typeof response.data === "object") {
        Object.keys(response.data).forEach((key) => {
          if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
            positions = [...positions, ...response.data[key].positions];

            if (
              response.data[key].drivers_with_colors &&
              Array.isArray(response.data[key].drivers_with_colors)
            ) {
              allDrivers = [
                ...allDrivers,
                ...response.data[key].drivers_with_colors,
              ];
            }
          }
        });

        const uniqueDrivers = allDrivers.filter(
          (driver, index, self) =>
            index === self.findIndex((d) => d.name === driver.name),
        );

        setMapData(positions);
        setDriversWithColors(uniqueDrivers);
      }

      if (response.data.events_data) {
        let eventsData: ClientEvents[] = [];
        Object.keys(response.data.events_data).forEach((key) => {
          eventsData = [...eventsData, ...response.data.events_data[key]];
        });
        setEvents(eventsData);
      }
    } catch (err) {
      setMapData([]);
      setEvents([]);
      setDriversWithColors([]);
      errorHandler(err, "Erro ao gerar relatório");
    }
  };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      <Header title="Rota" />
      <div className="flex-col md:grid md:grid-cols-4 gap-3 h-full w-full">
        <div className="w-full md:col-span-1">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <FormField
                control={form.control}
                name="vehicle_id"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Veículo</FormLabel>
                    <VehicleSelect
                      value={field.value}
                      onChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DateTimeField
                name="start_period"
                form={form}
                label="Período inicial"
              />

              <DateTimeField
                name="end_period"
                form={form}
                label="Período final"
              />

              <ReportButton loading={form.formState.isSubmitting} />
            </form>
          </Form>
        </div>
        <div className="w-full h-[300px] md:h-full md:col-span-3 pt-3 md:pt-0 ">
          <div className="h-full bg-gray-200">
            <MapStateProvider>
              <Map
                positions={mapData}
                events={events}
                showAnimation={true}
                displayEventsAndMarkersOptions={true}
                driversWithColors={driversWithColors}
              />
            </MapStateProvider>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapReport;
